import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Tabs
//
// For entity detail tabs and filter tabs.
// Design: §6.9 Navigation — Tabs
// ─────────────────────────────────────────────

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center gap-0.5 rounded-control bg-hover p-0.5",
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-control px-4 py-1.5",
      "text-[14px] font-medium text-text-muted transition-all",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
      "disabled:pointer-events-none disabled:opacity-50",
      "data-[state=active]:bg-card data-[state=active]:text-text-primary data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/**
 * ScrollableTabsList — keeps a large tab set on ONE row.
 *
 * Wrap a `TabsList` (with `flex-nowrap`) in this: overflowing tabs scroll
 * horizontally instead of wrapping, edge gradients + chevron buttons appear
 * only while there is more content in that direction, and the active tab is
 * scrolled into view whenever `activeValue` changes.
 */
function ScrollableTabsList({
  activeValue,
  className,
  children,
}: {
  activeValue?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);

  const updateShadows = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  React.useEffect(() => {
    updateShadows();
    const el = scrollerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateShadows);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateShadows]);

  // Keep the active tab visible when it changes (e.g. Previous/Next section nav).
  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[data-state="active"]');
    active?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [activeValue]);

  const nudge = (dir: 1 | -1) =>
    scrollerRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });

  return (
    <div className={cn("relative min-w-0", className)}>
      <div
        ref={scrollerRef}
        onScroll={updateShadows}
        className="overflow-x-auto rounded-control"
        style={{ scrollbarWidth: "none" }}
      >
        {children}
      </div>

      {canLeft && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 rounded-l-control bg-gradient-to-r from-hover to-transparent" />
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => nudge(-1)}
            className="absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-text-secondary hover:bg-card hover:text-text-primary"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}
      {canRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-control bg-gradient-to-l from-hover to-transparent" />
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => nudge(1)}
            className="absolute right-0.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-text-secondary hover:bg-card hover:text-text-primary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, ScrollableTabsList };
